import type {
  BusinessMetricId,
  BusinessMetricReadModel,
  OwnerDashboardDrillTarget,
  OwnerDashboardReason,
  OwnerDashboardStatus,
  OwnerOperatingDashboardReadModel,
} from "@billiard/contracts";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { OperatorIdentity } from "./api";
import { businessMetricDisplay } from "./businessMetricDictionary";
import {
  getOwnerOperatingDashboard,
  ownerDashboardCountLabel,
  ownerDashboardDrillHref,
  ownerDashboardFenLabel,
  ownerDashboardStatusDisplay,
  ownerOperatingDashboardQueryKey,
} from "./ownerOperatingDashboard";

export function ownerOperatingDashboardMatchesSelection(
  dashboard: OwnerOperatingDashboardReadModel | undefined,
  operator: Pick<OperatorIdentity, "tenantId" | "storeId">,
  businessDate: string,
): dashboard is OwnerOperatingDashboardReadModel {
  return Boolean(
    dashboard &&
      dashboard.tenantId === operator.tenantId &&
      dashboard.storeId === operator.storeId &&
      dashboard.businessDate === businessDate,
  );
}

function fact(label: string, value: string, detail?: string): React.JSX.Element {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function DashboardReasons({
  reasons,
  businessDate,
}: {
  reasons: readonly OwnerDashboardReason[];
  businessDate: string;
}): React.JSX.Element {
  if (reasons.length === 0) {
    return <p className="owner-dashboard-no-reason">当前没有需要处理的异常原因。</p>;
  }
  return (
    <div className="owner-dashboard-reasons">
      {reasons.map((reason, index) => (
        <article key={`${reason.drillTarget}:${reason.message}:${index}`}>
          <strong>{reason.message}</strong>
          <p>下一步：{reason.nextStep}</p>
          <Link to={ownerDashboardDrillHref(reason.drillTarget, businessDate)}>
            打开对应业务处理
          </Link>
        </article>
      ))}
    </div>
  );
}

function DashboardCard({
  title,
  description,
  status,
  reasons,
  facts,
  businessDate,
  defaultTarget,
  defaultLinkLabel,
}: {
  title: string;
  description: string;
  status: OwnerDashboardStatus;
  reasons: readonly OwnerDashboardReason[];
  facts: ReactNode;
  businessDate: string;
  defaultTarget: OwnerDashboardDrillTarget;
  defaultLinkLabel: string;
}): React.JSX.Element {
  const display = ownerDashboardStatusDisplay(status);
  return (
    <section className={`owner-dashboard-card tone-${display.tone}`} aria-label={title}>
      <header>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <span>{display.label}</span>
      </header>
      <dl className="owner-dashboard-facts">{facts}</dl>
      <DashboardReasons reasons={reasons} businessDate={businessDate} />
      <Link
        className="owner-dashboard-card-link"
        to={ownerDashboardDrillHref(defaultTarget, businessDate)}
      >
        {defaultLinkLabel}
      </Link>
    </section>
  );
}

function dutyStatusLabel(
  value: OwnerOperatingDashboardReadModel["duty"]["serviceDutyStatus"],
): string {
  if (value === "ACTIVE") return "服务职责有人承担";
  if (value === "INACTIVE") return "服务职责暂未有人承担";
  return "历史现场职责暂不可用";
}

function cashShiftStatusLabel(
  value: OwnerOperatingDashboardReadModel["duty"]["cashShiftStatus"],
): string {
  if (value === "NOT_STARTED") return "本营业日尚未开始收银班次";
  if (value === "OPEN") return "收银班次进行中";
  if (value === "HANDOVER_PENDING") return "收银班次等待交接";
  if (value === "CLOSED") return "收银班次已完成交接";
  return "历史收银班次暂不可用";
}

function nullableName(value: string | null, unavailable: boolean): string {
  if (value) return value;
  return unavailable ? "暂不可用" : "暂未安排";
}

function countWithUnit(value: number | null, unit: "张" | "笔" | "种"): string {
  return value === null ? "暂不可用" : `${value} ${unit}`;
}

function lowStockFact(value: number | null): React.JSX.Element {
  return (
    <div>
      <dt>低库存商品</dt>
      <dd>{countWithUnit(value, "种")}</dd>
      {value !== null && value > 0 ? (
        <Link to="/products?stock=attention">查看低库存与缺货商品</Link>
      ) : null}
    </div>
  );
}

function metric(
  dashboard: OwnerOperatingDashboardReadModel,
  id: BusinessMetricId,
): BusinessMetricReadModel | undefined {
  return dashboard.dictionary.metrics.find((candidate) => candidate.id === id);
}

function metricFact(
  dashboard: OwnerOperatingDashboardReadModel,
  id: Extract<BusinessMetricId, "CONFIRMED_RECEIPTS" | "REFUNDS" | "CASH_DIFFERENCE">,
): React.JSX.Element {
  const selected = metric(dashboard, id);
  if (!selected) return fact("指标暂不可用", "暂不可用", "权威指标缺失，页面没有按零处理");
  const display = businessMetricDisplay(selected);
  return fact(selected.label, display.valueLabel, display.availabilityLabel);
}

export function OwnerOperatingDashboardPanel({
  operator,
  businessDate,
  loader = getOwnerOperatingDashboard,
}: {
  operator: OperatorIdentity;
  businessDate: string;
  loader?: typeof getOwnerOperatingDashboard;
}): React.JSX.Element {
  const context = { tenantId: operator.tenantId, storeId: operator.storeId };
  const dashboard = useQuery({
    queryKey: ownerOperatingDashboardQueryKey(context, businessDate),
    queryFn: ({ signal }) => loader(businessDate, context, signal),
    enabled: Boolean(businessDate),
    retry: false,
    refetchInterval: 5_000,
  });
  const visibleDashboard =
    !dashboard.isError &&
    ownerOperatingDashboardMatchesSelection(dashboard.data, operator, businessDate)
      ? dashboard.data
      : undefined;
  const responseScopeChanged = Boolean(
    dashboard.data &&
      !ownerOperatingDashboardMatchesSelection(dashboard.data, operator, businessDate),
  );

  if (!businessDate) {
    return (
      <section className="owner-operating-dashboard-status" aria-live="polite">
        <strong>正在确认看板营业日</strong>
        <span>营业日确认后再读取经营看板，当前不会显示其他日期的状态。</span>
      </section>
    );
  }
  if (!visibleDashboard) {
    const failed = dashboard.isError || responseScopeChanged;
    return (
      <section
        className={`owner-operating-dashboard-status ${failed ? "is-error" : ""}`}
        role={failed ? "alert" : "status"}
        aria-live="polite"
      >
        {failed ? (
          <AlertTriangle size={20} aria-hidden="true" />
        ) : (
          <RefreshCw className="spin" size={20} aria-hidden="true" />
        )}
        <div>
          <strong>{failed ? "经营看板暂时无法核对" : "正在读取经营看板"}</strong>
          <span>
            {failed
              ? "旧日期或旧门店的看板已隐藏，请恢复连接后重新读取。"
              : `正在读取 ${businessDate} 的权威经营状态，不会沿用上一日期。`}
          </span>
        </div>
        {failed ? (
          <button
            className="button secondary compact"
            type="button"
            onClick={() => dashboard.refetch()}
          >
            <RefreshCw size={16} aria-hidden="true" /> 重新读取看板
          </button>
        ) : null}
      </section>
    );
  }

  const dutyUnavailable = visibleDashboard.duty.status === "UNAVAILABLE";
  return (
    <section className="owner-operating-dashboard" aria-label="老板店长经营看板">
      <div className="owner-operating-dashboard-heading">
        <div>
          <span className="eyebrow">营业日 {visibleDashboard.businessDate}</span>
          <h2>经营状况一眼看清</h2>
          <p>状态和下一步都来自本店权威事实；历史现场数据缺失时会明确显示暂不可用。</p>
        </div>
        <time dateTime={visibleDashboard.generatedAt}>
          生成于 {new Date(visibleDashboard.generatedAt).toLocaleString("zh-CN", { hour12: false })}
        </time>
      </div>
      <div className="owner-operating-dashboard-grid">
        <DashboardCard
          title="谁在当班"
          description="核对现场服务职责和收银交接是否有人承担。"
          status={visibleDashboard.duty.status}
          reasons={visibleDashboard.duty.reasons}
          businessDate={visibleDashboard.businessDate}
          defaultTarget="DUTY"
          defaultLinkLabel="查看当班职责"
          facts={
            <>
              {fact("现场服务", dutyStatusLabel(visibleDashboard.duty.serviceDutyStatus))}
              {fact(
                "服务负责人",
                nullableName(visibleDashboard.duty.serviceDutyAssignee, dutyUnavailable),
              )}
              {fact("收银交接", cashShiftStatusLabel(visibleDashboard.duty.cashShiftStatus))}
              {fact(
                "收银操作人",
                nullableName(visibleDashboard.duty.cashShiftOperator, dutyUnavailable),
              )}
              {fact(
                "未完成认领",
                ownerDashboardCountLabel(visibleDashboard.duty.incompleteClaimedCount),
              )}
              {fact(
                "无人处理请求",
                ownerDashboardCountLabel(visibleDashboard.duty.unattendedServiceRequestCount),
              )}
            </>
          }
        />
        <DashboardCard
          title="球台经营"
          description="查看球台使用、待清洁和长时开台提醒。"
          status={visibleDashboard.tables.status}
          reasons={visibleDashboard.tables.reasons}
          businessDate={visibleDashboard.businessDate}
          defaultTarget="TABLES"
          defaultLinkLabel="查看前台球台"
          facts={
            <>
              {fact("营业球台", countWithUnit(visibleDashboard.tables.enabledCount, "张"))}
              {fact("正在使用", countWithUnit(visibleDashboard.tables.inUseCount, "张"))}
              {fact("等待清洁", countWithUnit(visibleDashboard.tables.awaitingCleaningCount, "张"))}
              {fact(
                "开台预警",
                countWithUnit(visibleDashboard.tables.warningSessionCount, "张"),
                `严重提醒 ${countWithUnit(visibleDashboard.tables.criticalSessionCount, "张")}`,
              )}
              {fact(
                "球台应收",
                ownerDashboardFenLabel(visibleDashboard.tables.tableFeeReceivableFen),
              )}
            </>
          }
        />
        <DashboardCard
          title="资金健康"
          description="实收、退款、现金差异分别展示，不与订单应收互相抵消。"
          status={visibleDashboard.funds.status}
          reasons={visibleDashboard.funds.reasons}
          businessDate={visibleDashboard.businessDate}
          defaultTarget="ORDERS"
          defaultLinkLabel="查看订单资金明细"
          facts={
            <>
              {metricFact(visibleDashboard, "CONFIRMED_RECEIPTS")}
              {metricFact(visibleDashboard, "REFUNDS")}
              {metricFact(visibleDashboard, "CASH_DIFFERENCE")}
              {fact(
                "不可用指标",
                ownerDashboardCountLabel(visibleDashboard.funds.unavailableMetricCount),
                `部分可用 ${ownerDashboardCountLabel(visibleDashboard.funds.partialMetricCount)}`,
              )}
            </>
          }
        />
        <DashboardCard
          title="关键事项"
          description="集中查看需要老板或店长关注的核对、冲突和库存事项。"
          status={visibleDashboard.keyItems.status}
          reasons={visibleDashboard.keyItems.reasons}
          businessDate={visibleDashboard.businessDate}
          defaultTarget="REVIEW"
          defaultLinkLabel="打开关键事项"
          facts={
            <>
              {fact(
                "待人工核对",
                countWithUnit(visibleDashboard.keyItems.pendingManualReviewCount, "笔"),
                visibleDashboard.keyItems.manualReviewCapacityExceeded === null
                  ? "容量状态暂不可用"
                  : visibleDashboard.keyItems.manualReviewCapacityExceeded
                    ? "待办已超过页面容量，请立即处理"
                    : "待办未超过页面容量",
              )}
              {fact(
                "同步冲突订单",
                countWithUnit(visibleDashboard.keyItems.syncConflictOrderCount, "笔"),
              )}
              {lowStockFact(visibleDashboard.keyItems.lowStockProductCount)}
            </>
          }
        />
      </div>
      <aside className="owner-dashboard-store-comparison" aria-label="门店对比状态">
        <strong>多店对比：暂不可用</strong>
        <span>{visibleDashboard.storeComparison.reason}</span>
      </aside>
    </section>
  );
}
